/**
 * ABRIR A CAIXA DE ESCREVER DO BALÃO — um lugar só, dois gestos.
 *
 * ── O PONTO (`T8.10`) ────────────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"Não ficou claro como escrever dentro do balão: **selecioná-lo com clique duplo
 * não abriu nenhuma edição de texto**."*
 *
 * E era verdade dos dois lados: o clique duplo não fazia nada, e a edição existia — num botão de
 * lápis dentro do menu do item, que aparece só depois de o balão estar selecionado. Quem não
 * encontrou o menu (e o `T4.7` é um relatório inteiro de não encontrá-lo) não tinha como escrever.
 *
 * ── POR QUE ISTO É UM ARQUIVO, E NÃO DUAS CÓPIAS DE TRÊS LINHAS ──────────────────────────────
 *
 * Porque a partir de hoje dois gestos abrem a mesma coisa — o botão do menu e o clique duplo — e
 * duas cópias divergem no primeiro dia em que alguém muda uma. Divergir aqui é pior que em outro
 * lugar: os dois caminhos parecem funcionar, e a diferença aparece só para quem usou o gesto que
 * ninguém atualizou.
 *
 * O `showMathKeyboard` está ligado porque este produto é usado em escola e a mesma caixa serve a
 * uma fala e a uma fórmula — é a decisão que já estava no botão, e ela vem junto.
 */
import { Dispatch, SetStateAction } from "react";
import { PopUpInfo } from "../components/confirm-pop-up";

/** O mínimo que esta função precisa de um balão: ler e escrever o texto dele. */
export interface TemTexto {
  Text(): string;
  Text(valor: string): void;
}

/** A frase da caixa. Uma só, para os dois gestos dizerem a mesma coisa. */
export const PERGUNTA_DO_BALAO = "Escreva o que vai dentro do balão";

/**
 * ── POR QUE O DUPLO CLIQUE É CONTADO AQUI, E NÃO PELO KONVA — MEDIDO EM 26/09 ────────────────
 *
 * O `onDblClick` do Konva foi a primeira tentativa, e ela **não funciona neste item**. A medição,
 * com um espião contando eventos no palco e dois cliques de verdade a 120 ms um do outro:
 *
 *     click: 2   ·   dblclick: 0   ·   nós clicados: [86, 90]
 *
 * **Os dois cliques chegam, e chegam em nós DIFERENTES.** O primeiro clique seleciona o balão, o
 * React redesenha, e o nó do Konva é substituído — id 86 vira 90. O Konva só sintetiza `dblclick`
 * quando os dois cliques caem no MESMO nó, então ele nunca sintetiza: o gesto do operador era
 * impossível de completar, e não é o manipulador que estava faltando.
 *
 * (Que o manipulador estava certo também foi medido: `fire('dblclick')` no grupo abria a caixa com o
 * texto que já estava lá. Duas perguntas diferentes, dois instrumentos — lição 49.)
 *
 * O CONSERTO CONTA O INTERVALO, e a chave dele está explicada no mapa abaixo.
 *
 * ── A JANELA É 500 ms, E NÃO OS 400 DO KONVA ─────────────────────────────────────────────────
 *
 * 500 ms é o limiar que os navegadores usam para o próprio `dblclick`, e é o que a pessoa já tem na
 * mão em qualquer outro programa. Os 400 do Konva (`Konva.dblClickWindow`) são mais apertados que
 * isso, e apertar o gesto num produto para criança de escola erra para o lado ruim: o clique duplo
 * que não conta parece que o produto não faz nada — que é literalmente a queixa deste ponto.
 *
 * O que custa se 500 for demais: dois cliques deliberados e lentos no MESMO balão abrem a caixa de
 * escrever sem ninguém pedir. A caixa tem Cancelar, vem preenchida com o texto que já estava, e
 * fechar não mexe em nada. É o erro mais barato dos dois.
 */
export const JANELA_DE_DUPLO_CLIQUE = 500;

/**
 * O RELÓGIO POR `id`, E NÃO POR INSTÂNCIA — e esta foi a SEGUNDA medição a corrigir o conserto.
 *
 * Guardar o instante do último clique num campo do próprio `TextBubble` não funcionou, e a contagem
 * de eventos mostrou por quê: depois da primeira tentativa o resultado continuou
 * `click: 2 · dblclick: 0 · caixa: não abriu`.
 *
 * A causa está em `layer.class.ts`: `copy()` faz `items.map((item) => DraggableItemFactory.clone(...))`
 * — **cada redesenho troca a instância de todo item da página**. E o editor redesenha a cada clique,
 * porque selecionar chama `setComic(comic.copy())`. Então o campo que eu tinha acabado de escrever
 * era jogado fora antes do segundo clique, exatamente como o nó do Konva.
 *
 * O que SOBREVIVE ao clone é o `id` do item — ele viaja em `toObj()`, e é a chave do documento salvo.
 * Daí o mapa: a chave é o `id`, e não o objeto.
 *
 * O mapa não cresce sem limite porque só recebe item que alguém clicou, e uma entrada é um número.
 */
const ULTIMO_CLIQUE = new Map<string, number>();

/**
 * Registra um clique num item e diz se ele fecha um duplo.
 *
 * Devolver booleano em vez de abrir a caixa aqui é de propósito: quem chama decide o que o duplo
 * clique faz, e este arquivo não precisa saber.
 */
export function ehDuploClique(id: string, agora = Date.now()): boolean {
  const anterior = ULTIMO_CLIQUE.get(id) ?? 0;
  ULTIMO_CLIQUE.set(id, agora);
  ultimoIntervalo = anterior > 0 ? agora - anterior : null;
  return anterior > 0 && ultimoIntervalo! <= JANELA_DE_DUPLO_CLIQUE;
}

/**
 * O INTERVALO DA ÚLTIMA DUPLA DE CLIQUES, em milissegundos, ou `null` na primeira.
 *
 * Existe para a MEDIÇÃO, e não para a tela. Sem ele, um duplo clique que não abre a caixa tem quatro
 * causas possíveis — o gesto não chegou, chegou em nós diferentes, o `id` mudou, ou o intervalo
 * estourou — e nenhuma delas se distingue das outras de fora. Foi a falta deste número que me fez
 * medir a latência do meu próprio instrumento e chamar isso de defeito do produto.
 */
export let ultimoIntervalo: number | null = null;

/** Só para o teste poder medir duas sequências sem uma contaminar a outra. */
export function esqueceOsCliques() {
  ULTIMO_CLIQUE.clear();
  ultimoIntervalo = null;
}

export function abrirEdicaoDeTexto(
  item: TemTexto,
  setPopUpInfo: Dispatch<SetStateAction<PopUpInfo | undefined>>
) {
  setPopUpInfo({
    type: "input",
    text: PERGUNTA_DO_BALAO,
    close: () => setPopUpInfo(undefined),
    confirm: (input) => {
      // Texto vazio confirmado é apagar o texto, e isso é legítimo: a pessoa pode querer o balão
      // vazio para desenhar por cima. O que NÃO se faz é fechar sem mexer — daí o `close` acima.
      item.Text(input);
      setPopUpInfo(undefined);
    },
    showMathKeyboard: true,
    inputDefault: item.Text(),
  });
}
